/**
 * Value Type System
 * Defines the structure for column types in summary spreadsheets
 */

import { DisplayFormat } from './displayFormats';

export type ValueKind = 'simple' | 'virtual';

/**
 * Data source mapping for simple value types
 * Defines where to get the data from
 */
export type DataSourceMapping =
  | { type: 'field'; path: string } // Direct field: "company.name"
  | { type: 'nested'; path: string } // Nested object: "valuation.unrealized_value"
  | { type: 'aggregate'; operation: 'sum' | 'avg' | 'min' | 'max' | 'count'; path: string }
  | { type: 'custom'; resolver: string }; // Custom function

/**
 * Calculation definitions for virtual value types
 */
export type CalculationDefinition =
  | ExcelFormulaCalculation
  | AggregationCalculation
  | FunctionCalculation
  | CustomCalculation;

export interface ExcelFormulaCalculation {
  strategy: 'excel-formula';
  template: string; // e.g., "={realized_value}+{unrealized_value}"
  references?: CellReference[];
}

export interface AggregationCalculation {
  strategy: 'aggregation';
  operation: 'sum' | 'avg' | 'min' | 'max' | 'count';
  over: string; // What to aggregate: 'investments', 'securities', etc.
  valueType: string; // Which value type to aggregate
  filters?: FilterCondition[];
}

export interface FunctionCalculation {
  strategy: 'function';
  name: string; // 'calculateIRR', 'calculateMOIC', etc.
  params: Record<string, string | number | boolean>;
}

export interface CustomCalculation {
  strategy: 'custom';
  evaluatorRef: string;
  config?: Record<string, unknown>;
}

export interface FilterCondition {
  field: string;
  operator: '=' | '!=' | '>' | '>=' | '<' | '<=';
  value: string | number | boolean;
}

/**
 * Cell reference types for formula resolution
 */
export type CellReference =
  | HorizontalCellReference
  | VerticalRangeReference
  | HorizontalRangeReference
  | SpecificCellReference;

export interface HorizontalCellReference {
  mode: 'horizontal-cell';
  placeholder: string;
  valueTypeId: string;
}

export interface VerticalRangeReference {
  mode: 'vertical-range';
  placeholder: string;
  valueTypeId: string;
  rowOffset: number;
  rowCount: number;
}

export interface HorizontalRangeReference {
  mode: 'horizontal-range';
  placeholder: string;
  valueTypeIds: string[];
}

export interface SpecificCellReference {
  mode: 'specific-cell';
  placeholder: string;
  cellAddress: string;
}

export type ValueTypeCategory =
  | 'identifier' // Company name, fund name
  | 'dates' // Investment dates, valuation dates
  | 'ownership' // Ownership percentages
  | 'values' // Financial values (realized, unrealized, total)
  | 'metrics' // Calculated metrics (IRR, MOIC)
  | 'cap_table' // Cap table data (securities, shares, preferences)
  | 'financials' // Financial statement data (income statement, balance sheet, margins)
  | 'cash_flows' // Cash flow data (distributions, net cash flows)
  | 'valuation' // Valuation approach-specific data (GPC, backsolve, DCF, calibration)
  | 'breakpoints' // Breakpoint analysis data
  | 'process' // Documents & process management
  | 'custom'; // User-defined

/**
 * Value Type definition
 * Core interface for defining column types
 */
export interface ValueType {
  id: string; // Unique identifier: "total_value", "moic", "company_name"
  displayName: string; // Human-readable: "Total Value", "MOIC", "Company Name"
  displayFormat: DisplayFormat;
  kind: ValueKind;
  dependsOn: string[]; // Array of value type IDs this depends on
  dataSource?: DataSourceMapping; // For simple values - where to get data
  calculation?: CalculationDefinition; // For virtual values - how to calculate
  metadata?: ValueTypeMetadata;
}

export interface ValueTypeMetadata {
  description?: string;
  category?: ValueTypeCategory;
  tags?: string[];
  isRequired?: boolean; // Cannot be removed from spreadsheet
  isLocked?: boolean; // Cannot be modified
  isDraftSensitive?: boolean; // Should show "DRAFT" when valuation not published
  summaryType?: 'firm' | 'fund' | 'both'; // Which summary types this applies to
  isClientOnly?: boolean; // Rendered client-side with no backend row value; not sortable/filterable
}

/**
 * Type guard to check if a value type is rendered client-side only (no backend value),
 * so it cannot participate in the server-backed sort/filter flow.
 */
export function isClientOnlyValueType(valueType: ValueType): boolean {
  return valueType.metadata?.isClientOnly === true;
}

/**
 * Type guard to check if a value type is virtual (calculated)
 */
export function isVirtualValueType(valueType: ValueType): boolean {
  return valueType.kind === 'virtual';
}

/**
 * Type guard to check if a value type is draft-sensitive
 */
export function isDraftSensitiveValueType(valueType: ValueType): boolean {
  return valueType.metadata?.isDraftSensitive === true;
}
